import { CreditCard } from 'lucide-react';
import type { AdminCompanyPlan } from 'app/javascript/types/admin';
import FacialUploadBonusControls from 'app/javascript/components/admin/company_health/FacialUploadBonusControls';
import PlanGrantControls from 'app/javascript/components/admin/company_health/PlanGrantControls';
import HealthPanel from 'app/javascript/components/admin/company_health/HealthPanel';
import HealthRow from 'app/javascript/components/admin/company_health/HealthRow';
import UsageMeter from 'app/javascript/components/admin/company_health/UsageMeter';
import { Badge } from 'app/javascript/components/ui/badge';
import { Separator } from 'app/javascript/components/ui/separator';
import { formatBrDate } from 'app/javascript/utils/dates';

const SUBSCRIPTION_LABELS: Record<string, string> = {
  incomplete: 'incompleta',
  active: 'ativa',
  past_due: 'em atraso',
  canceled: 'cancelada',
};

const FEATURE_LABELS: Record<string, string> = {
  facial_recognition: 'facial',
  billing: 'cobrança',
  analytics: 'analytics',
  advanced_reports: 'relatórios',
  priority_support: 'suporte',
};

function formatPrice(cents: number | null) {
  if (cents === null) return '—';
  return (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

const MANAGED_BY_LABELS: Record<string, string> = {
  app_store: 'App Store',
  play_store: 'Google Play',
  site: 'site (Stripe)',
  granted: 'concessão',
  none: 'nenhum',
};

interface PlanPanelProps {
  plan: AdminCompanyPlan;
  // Lives in the members section, but only means something next to the limit.
  memberCount: number;
  slug: string;
  className?: string;
}

export default function PlanPanel({ plan, memberCount, slug, className }: PlanPanelProps) {
  const features = plan.enabled_features.map((key) => FEATURE_LABELS[key] ?? key);

  return (
    <HealthPanel
      className={className}
      icon={<CreditCard className="size-4" />}
      title="Plano"
      action={
        <Badge variant={plan.free ? 'secondary' : 'default'}>{plan.name ?? 'desconhecido'}</Badge>
      }
    >
      <UsageMeter label="Membros ativos" used={memberCount} limit={plan.member_limit} />
      <UsageMeter
        label="Uploads de facial"
        used={plan.facial_uploads_used}
        limit={plan.facial_upload_limit}
      />
      <FacialUploadBonusControls
        slug={slug}
        baseLimit={plan.facial_upload_base_limit}
        extraCredits={plan.facial_upload_extra_credits}
      />
      <Separator className="my-3" />
      <HealthRow label="Mensalidade" value={formatPrice(plan.price_cents)} />
      <HealthRow
        label="Assinatura"
        value={
          plan.subscription_status ? (
            <Badge
              variant={plan.subscription_status === 'past_due' ? 'destructive' : 'secondary'}
            >
              {SUBSCRIPTION_LABELS[plan.subscription_status] ?? plan.subscription_status}
            </Badge>
          ) : (
            <span className="text-muted-foreground">nenhuma</span>
          )
        }
      />
      {plan.managed_by && (
        <HealthRow
          label="Gerenciado por"
          value={MANAGED_BY_LABELS[plan.managed_by] ?? plan.managed_by}
        />
      )}
      {plan.current_period_end && (
        <HealthRow label="Ciclo até" value={formatBrDate(plan.current_period_end.slice(0, 10))} />
      )}
      <HealthRow
        label="Recursos"
        value={
          features.length > 0 ? (
            <span className="flex flex-wrap justify-end gap-1">
              {features.map((feature) => (
                <Badge key={feature} variant="outline">
                  {feature}
                </Badge>
              ))}
            </span>
          ) : (
            <span className="text-muted-foreground">nenhum</span>
          )
        }
      />
      <Separator className="my-3" />
      <PlanGrantControls slug={slug} managedBy={plan.managed_by} />
    </HealthPanel>
  );
}
